import React, { useCallback, useState, useEffect, ChangeEvent } from 'react';
import { TextField, Paper, Button, Icon, makeStyles, FormControl, Select, MenuItem, FormHelperText, Theme } from '@material-ui/core';
import { TFunction } from 'i18next';
import clsx from 'clsx';
import produce from 'immer';
import { MonitorSeverity } from '@moderepo/mode-apis';
import { useModeFormStyle, SelectInputOption, capitalizeAll, capitalizeFirst } from '@moderepo/mode-app-components';
import { useEditMonitorStyle } from './EditMonitorStyle';
import * as RCUtils from '../../../containers/robot-cloud/RCUtils';


const useStyle = makeStyles((theme: Theme) => {
    return {

        inputControlGroup: {
            display                     : 'flex',
            alignItems                  : 'center',
            flexDirection               : 'column',
            [theme.breakpoints.up('sm')]: {
                flexDirection: 'row',
            },
            padding: 0,

            '&.disabled-state': {
                marginBottom: 0,
            },

            '& .label': {
                fontWeight: '500',
                fontSize  : 'larger',
            },
        },

        severityInputControl: {
            width: '100%',

            '&.name': {
                flex: 1,
            },

            '&.severity': {
                width                       : '100%',
                [theme.breakpoints.up('sm')]: {
                    width     : 'auto',
                    minWidth  : 150,
                    marginLeft: theme.spacing(2),
                },
            },
        },
    };
});



/**
 * Custom hook to create an Array of Monitor severities options used for Severity input
 * @param trans
 */
const useMonitorSeverityOptions = (trans: TFunction): readonly SelectInputOption<MonitorSeverity>[] => {
    const [options, setOptions] = useState<readonly SelectInputOption<MonitorSeverity>[]>([]);

    useEffect(() => {
        setOptions((Object.values(MonitorSeverity).filter((v: any): boolean => {
            return typeof v === 'number';
        }) as MonitorSeverity[]).map((severity: MonitorSeverity): SelectInputOption<MonitorSeverity> => {
            return {
                label: capitalizeAll(trans(RCUtils.getMonitorSeverityName(severity))),
                value: severity,
            };
        }));
    }, [trans]);

    return options;
};


export interface MonitorSetupInputErrors {
}


/**
 * The BASE interface for EditMonitorStepsProps. This define the common required props for all EditMonitorSteps. Each specific EditMonitorSteps
 * will have their own additional props.
 */
export interface EditMonitorStepProps {
    readonly projectId: number;
    readonly isNewMonitor: boolean;
    readonly isLastStep: boolean;
    readonly trans: TFunction;
    readonly canGoBack: boolean;
    readonly onPrevStepClick: ()=> void;
    readonly onNextStepClick: ()=> void;
    readonly onGotoLastStepClick: ()=> void;
    readonly onStepError: (hasError: boolean)=> void;
}


interface EditMonitorInfoStepProps extends EditMonitorStepProps {
    readonly monitorName: string | undefined;
    readonly monitorDescription: string | undefined;
    readonly monitorSeverity: MonitorSeverity | undefined;
    readonly onStepCompleted: (monitorName: string, monitorDescription: string, monitorSeverity: MonitorSeverity)=> void;
}


/**
 * Errors fields for Monitor Info setup step
 */
interface MonitorInfoInputErrors extends MonitorSetupInputErrors {
    name?: string;
    description?: string;
    severity?: string;
}


/**
 * This is the HTML for the Setup Monitor Info step content
 * @param props
 */
export const EditMonitorInfoStep = (props: EditMonitorInfoStepProps) => {

    const { trans } = props;
    const formClasses = useModeFormStyle();
    const globalClasses = useEditMonitorStyle();

    const monitorSeverityOptions = useMonitorSeverityOptions(trans);


    // This holds the current value of the user input. We will initialize it with the value from props.
    const [monitorNameInput, setMonitorNameInput] = useState<string>(props.monitorName || '');
    const [monitorDescriptionInput, setMonitorDescriptionInput] = useState<string>(props.monitorDescription || '');
    const [monitorSeverityInput, setMonitorSeverityInput] = useState<MonitorSeverity | undefined>(props.monitorSeverity);
    const [inputErrors, setInputErrors] = useState<MonitorInfoInputErrors>({ });

    const classes = useStyle();


    /**
     * Validate Monitor Info form and dispatch step completed if success
     */
    const validateInputs = useCallback((dispatchOnComplete: boolean = true, dispatchOnError: boolean = true): boolean => {

        const newInputErrors: MonitorInfoInputErrors = { };

        const newMonitorName = monitorNameInput.trim();
        const newMonitorDescription = monitorDescriptionInput.trim();
        const newSeverity = monitorSeverityInput;

        // Validate the values and set the error if any of the input has error
        if (!newMonitorName) {
            newInputErrors.name = 'error.input.monitor_info.name.empty';
        }
        if (!newMonitorDescription) {
            newInputErrors.description = 'error.input.monitor_info.description.empty';
        }
        if (!newSeverity) {
            newInputErrors.severity = 'error.input.monitor_info.severity.empty';
        }

        // Update the local inputErrors
        setInputErrors(newInputErrors);


        // If there is AT LEAST 1 field in the newInputError, this Step is considered to contain errors.
        const hasErrors = Object.keys(newInputErrors).length > 0;


        // If 'dispatchOnError' is specified, dispatch the error or null to the container
        if (dispatchOnError) {
            props.onStepError(hasErrors);
        }


        if (!hasErrors && dispatchOnComplete && newSeverity) {
            // Only dispatch onComplete if dispatchOnComplete is true. Sometime we just want to validate the input but not submit the form.
            props.onStepCompleted(newMonitorName, newMonitorDescription, newSeverity);
        }

        return !hasErrors;
    }, [props, monitorNameInput, monitorDescriptionInput, monitorSeverityInput]);



    return (
        <div className={globalClasses.stepContentBody}>
            <div className={clsx(globalClasses.stepContentForm)}>
                <div className={clsx(globalClasses.stepSubtitle)}>
                    {trans('create_monitor.step_info.subtitle')}
                </div>
                {monitorSeverityOptions.length > 0 && (
                    <>
                        <Paper elevation={1} className={clsx(globalClasses.formControlGroup)}>
                            <TextField
                                className={clsx(formClasses.formInputField, globalClasses.formControl, 'name')}
                                variant="standard"
                                type="text"
                                required
                                autoFocus
                                label={capitalizeAll(trans('label.monitor_name'))}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                InputProps={{
                                    inputProps: {
                                        minLength: RCUtils.MONITOR_NAME_LENGTH_MIN,
                                        maxLength: RCUtils.MONITOR_NAME_LENGTH_MAX,
                                    },
                                }}
                                value={monitorNameInput || ''}
                                error={Boolean(inputErrors.name)}
                                helperText={inputErrors.name ? trans(inputErrors.name) : undefined}
                                onChange={(event: React.FormEvent) => {
                                    // Input changed so clear the error and update the state
                                    props.onStepError(false);
                                    setInputErrors(produce(inputErrors, (draft: any) => {
                                        delete draft.name;
                                    }));
                                    setMonitorNameInput((event.currentTarget as HTMLSelectElement).value);
                                }}
                                onBlur={() => {
                                    // on done editing, validate input but don't submit it until the user click next
                                    // validateInputs(false);
                                }}
                            />
                            <TextField
                                className={clsx(formClasses.formInputField, globalClasses.formControl, 'description')}
                                variant="standard"
                                type="text"
                                required
                                label={capitalizeAll(trans('label.monitor_description'))}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                multiline
                                value={monitorDescriptionInput || ''}
                                error={Boolean(inputErrors.description)}
                                helperText={inputErrors.description ? trans(inputErrors.description) : undefined}
                                onChange={(event: React.FormEvent) => {
                                    // Input changed so clear the error and update the state
                                    props.onStepError(false);
                                    setInputErrors(produce(inputErrors, (draft: any) => {
                                        delete draft.description;
                                    }));
                                    setMonitorDescriptionInput((event.currentTarget as HTMLSelectElement).value);
                                }}
                                onBlur={() => {
                                    // on done editing, validate input but don't submit it until the user click next
                                    // validateInputs(false);
                                }}
                            />
                        </Paper>

                        <div className={clsx(globalClasses.formControlGroup, classes.inputControlGroup)}>
                            <div className="label">{capitalizeFirst(trans('create_monitor.step_info.severity_label'))}</div>
                            <FormControl
                                className={clsx(globalClasses.formControl, classes.severityInputControl, 'severity')}
                                error={Boolean(inputErrors.severity)}
                            >
                                <Select
                                    displayEmpty
                                    value={monitorSeverityInput || ''}
                                    onChange={(event: ChangeEvent<{value: unknown}>) => {
                                        // Input changed so clear the error and update the state
                                        props.onStepError(false);
                                        setInputErrors(produce(inputErrors, (draft: any) => {
                                            delete draft.severity;
                                        }));
                                        setMonitorSeverityInput(event.target.value as MonitorSeverity);
                                    }}
                                    onBlur={() => {
                                        // on done editing, validate input but don't submit it until the user click next
                                        // validateInputs(false);
                                    }}
                                >
                                    <MenuItem key="empty" value="" disabled>
                                        {capitalizeFirst(trans('label.select_one'))}
                                    </MenuItem>
                                    {monitorSeverityOptions.map((option: SelectInputOption<MonitorSeverity>) => {
                                        return (
                                            <MenuItem key={option.label} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>

                                {inputErrors.severity && (
                                    <FormHelperText>
                                        {trans(inputErrors.severity)}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </div>
                    </>
                )}
            </div>

            <div className={globalClasses.stepContentActionsContainer}>
                <Button
                    disabled={!props.canGoBack}
                    onClick={() => {
                        // Validate the inputs AND dispatch onComplete if successful so that we can save the user inputs before going back.
                        // However, if inputs are invalid, we will not dispatch error and just take the user back to the previous step. We will not
                        // save the user's input since it is invalid anyway.
                        validateInputs(true, false);
                        props.onPrevStepClick();
                    }}
                    className={globalClasses.navButton}
                    startIcon={<Icon>navigate_before</Icon>}
                >
                    {capitalizeAll(trans('label.back'))}
                </Button>
                <Button
                    onClick={() => {
                        if (validateInputs(true)) {
                            props.onNextStepClick();
                        }
                    }}
                    className={globalClasses.navButton}
                    endIcon={<Icon>navigate_next</Icon>}
                >
                    {capitalizeAll(trans(props.isLastStep ? 'label.finish' : 'label.next'))}
                </Button>
                {!props.isNewMonitor && (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                            if (props.onGotoLastStepClick && validateInputs(true)) {
                                props.onGotoLastStepClick();
                            }
                        }}
                        className={clsx(globalClasses.navButton, 'finish')}
                    >
                        {capitalizeAll(trans('label.finish'))}
                    </Button>
                )}
            </div>
        </div>
    );
};
